import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getReimbursements, getReimbursement, createReimbursement,
  uploadReimbursementAttachment, getExpenseCategories, getTasks, getStaff,
  approveReimbursement, rejectReimbursement, reviewReimbursement,
  returnReimbursement, resubmitReimbursement, reopenReimbursement, uploadsUrl,
} from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateDdMmmYy } from '../../utils/format';
import { extractErrorMessage } from '../../utils/errorMessage';

/**
 * Reimbursements on a phone.
 *
 * This is the screen the mobile build exists for: filing a claim means
 * photographing a receipt, and the camera is already in your hand. So the
 * capture path is the spine of the form — everything else (task, category,
 * amount) is arranged around it rather than the other way round.
 *
 * Three full-screen views instead of the desktop's stacked modals. A modal
 * inside a 375px viewport is just a page with wasted edges, and a real view
 * swap keeps the on-screen keyboard from fighting a scroll container.
 */

type Status = 'PENDING' | 'REVIEWED' | 'RETURNED' | 'APPROVED' | 'REJECTED' | 'PAID';

interface ExpenseCategory { id: number; name: string }
// categoryName disambiguates two open tasks sharing a name on the same client
// — see the note on TaskOption in the desktop Reimbursements page.
interface TaskOption { id: number; taskId: string; taskName: string; clientName?: string; categoryName?: string }
interface RAttachment { id: number; fileName: string; originalName: string; mimeType: string }
interface RItem {
  id: number; description: string; amount: number; date: string;
  category: ExpenseCategory; attachments: RAttachment[];
}
interface Reimbursement {
  id: number; claimNumber: string;
  staff: { id: number; staffName: string };
  task: {
    id: number; taskId: string; taskName: string;
    client?: { clientName: string } | null;
    category?: { id: number; name: string } | null;
  };
  notes?: string;
  status: Status;
  reviewedBy?: { id: number; staffName: string };
  reviewedAt?: string;
  returnReason?: string;
  approvedBy?: { id: number; staffName: string };
  approvedAt?: string;
  rejectionReason?: string;
  paidOn?: string;
  paymentMode?: string;
  paymentReference?: string;
  items: RItem[];
  createdAt: string;
}

/** Draft line item. Amount stays a string until submit, as on desktop. */
interface DraftItem { description: string; amount: string; date: string; categoryId: string; files: File[] }

const STATUS_LABEL: Record<Status, string> = {
  PENDING: 'Pending review', REVIEWED: 'Reviewed', RETURNED: 'Returned',
  APPROVED: 'Approved', REJECTED: 'Rejected', PAID: 'Paid',
};
const STATUS_PILL: Record<Status, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  REVIEWED: 'bg-blue-100 text-blue-700',
  RETURNED: 'bg-orange-500 text-white',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
  PAID: 'bg-emerald-100 text-emerald-800',
};

// CLAUDE.md: UI amounts are whole rupees, no decimals.
const fmt = (n: number) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const claimTotal = (r: Reimbursement) => r.items.reduce((s, i) => s + Number(i.amount), 0);
const today = () => new Date().toISOString().slice(0, 10);
const emptyDraft = (): DraftItem => ({ description: '', amount: '', date: today(), categoryId: '', files: [] });

// text-base (16px) is deliberate: iOS Safari zooms the viewport on focus for
// anything smaller, and the zoom never fully unwinds.
const INPUT = 'w-full min-h-[44px] px-3 py-2.5 text-base bg-white border border-gray-300 rounded-lg ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const LABEL = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';
const BTN = 'min-h-[44px] px-4 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5';

/** Preview for a not-yet-uploaded file. Object URLs must be revoked or the
 *  blob stays pinned in memory — several full-size photos is real memory. */
const PendingThumb: React.FC<{ file: File; onRemove: () => void }> = ({ file, onRemove }) => {
  const [url, setUrl] = useState('');
  const isImage = file.type.startsWith('image/');

  useEffect(() => {
    if (!isImage) return;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file, isImage]);

  return (
    <div className="relative w-20 h-20 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden shrink-0">
      {isImage && url ? (
        <img src={url} alt={file.name} className="w-full h-full object-cover" />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center text-2xl">📄</span>
      )}
      <button
        type="button"
        aria-label={`Remove ${file.name}`}
        onClick={onRemove}
        className="absolute top-0 right-0 w-7 h-7 bg-black/60 text-white text-sm leading-none rounded-bl-lg"
      >
        ✕
      </button>
    </div>
  );
};

/**
 * The capture row. Two inputs, not one: `capture="environment"` opens the
 * rear camera straight away on iOS and Android, but it also *forbids* picking
 * an existing photo or a PDF, so the gallery/file path needs its own input.
 */
const ReceiptPicker: React.FC<{ id: string; onFiles: (files: File[]) => void }> = ({ id, onFiles }) => {
  const take = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) onFiles(files);
    e.target.value = ''; // allow re-picking the same file
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      <label className={`${BTN} bg-blue-600 text-white cursor-pointer`}>
        📷 Photo
        <input id={`${id}-cam`} type="file" className="hidden" accept="image/*" capture="environment" onChange={take} />
      </label>
      <label className={`${BTN} bg-white text-blue-700 border border-blue-200 cursor-pointer`}>
        📎 Upload
        <input id={`${id}-file`} type="file" className="hidden" multiple accept="image/*,application/pdf" onChange={take} />
      </label>
    </div>
  );
};

const StatusPill: React.FC<{ status: Status }> = ({ status }) => (
  <span className={`text-[11px] font-semibold px-2 py-1 rounded-full shrink-0 ${STATUS_PILL[status]}`}>
    {STATUS_LABEL[status]}
  </span>
);

const FILTERS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'OPEN', label: 'In progress' },
  { key: 'RETURNED', label: 'Returned' },
  { key: 'PAID', label: 'Paid' },
];

const MobileReimbursements: React.FC = () => {
  const { user, isAdmin, isHR } = useAuth();

  const [view, setView] = useState<'list' | 'create' | 'detail'>('list');
  const [claims, setClaims] = useState<Reimbursement[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [tasks, setTasks] = useState<TaskOption[]>([]);
  const [isPartner, setIsPartner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState('');

  // Create
  const [taskQuery, setTaskQuery] = useState('');
  const [taskId, setTaskId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [drafts, setDrafts] = useState<DraftItem[]>([emptyDraft()]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Detail
  const [detail, setDetail] = useState<Reimbursement | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [reasonFor, setReasonFor] = useState<'RETURN' | 'REJECT' | null>(null);
  const [reason, setReason] = useState('');

  const fetchAll = useCallback(async () => {
    setLoadError('');
    try {
      const [rRes, cRes, tRes] = await Promise.all([
        getReimbursements(), getExpenseCategories(), getTasks(),
      ]);
      setClaims(rRes.data);
      setCategories(cRes.data);
      setTasks(
        (tRes.data as any[])
          .filter((t) => t.status === 'OPEN')
          .map((t) => ({
            id: t.id, taskId: t.taskId, taskName: t.taskName,
            clientName: t.client?.clientName, categoryName: t.category?.name,
          })),
      );
    } catch (err: any) {
      setLoadError(extractErrorMessage(err) || 'Could not load your claims');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Partner status decides who may approve, exactly as the desktop page does
  // (isAdmin, or the signed-in staff row is flagged isPartner). Staff without
  // directory access get a failure here, which correctly means "not an approver".
  useEffect(() => {
    let cancelled = false;
    getStaff()
      .then((res) => {
        if (cancelled) return;
        setIsPartner(!!(res.data as any[]).find((s) => s.id === user?.staffId)?.isPartner);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.staffId]);

  const isApprover = isAdmin || isPartner;

  const visible = useMemo(() => {
    const rows = [...claims].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    if (filter === 'OPEN') return rows.filter((r) => ['PENDING', 'REVIEWED', 'APPROVED'].includes(r.status));
    if (filter) return rows.filter((r) => r.status === filter);
    return rows;
  }, [claims, filter]);

  const matchingTasks = useMemo(() => {
    const q = taskQuery.trim().toLowerCase();
    if (!q) return [];
    return tasks
      .filter((t) =>
        t.taskId.toLowerCase().includes(q) ||
        t.taskName.toLowerCase().includes(q) ||
        (t.categoryName || '').toLowerCase().includes(q) ||
        (t.clientName || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [tasks, taskQuery]);

  const selectedTask = tasks.find((t) => t.id === taskId);
  const draftTotal = drafts.reduce((s, d) => s + (Number(d.amount) || 0), 0);

  const patchDraft = (idx: number, patch: Partial<DraftItem>) =>
    setDrafts((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));

  const openCreate = () => {
    setTaskQuery(''); setTaskId(''); setNotes('');
    setDrafts([emptyDraft()]); setCreateError('');
    setView('create');
  };

  const openDetail = async (r: Reimbursement) => {
    setDetail(r); // show the list copy instantly
    setView('detail');
    setDetailLoading(true);
    try {
      const res = await getReimbursement(r.id);
      setDetail(res.data);
    } catch {
      // Claim may have been deleted elsewhere; keep the optimistic copy.
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Create ─────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    setCreateError('');
    if (!taskId) { setCreateError('Please select a task'); return; }
    const valid = drafts.filter((d) => d.description && d.amount && d.date && d.categoryId);
    if (valid.length === 0) { setCreateError('Add at least one complete line item'); return; }
    setCreating(true);
    try {
      const res = await createReimbursement({
        taskId: String(taskId),
        notes,
        items: valid.map((d) => ({
          description: d.description,
          amount: Number(d.amount),
          date: d.date,
          categoryId: Number(d.categoryId),
        })),
      });
      const created: Reimbursement = res.data;
      // Receipts upload one at a time against the server-side item ids, which
      // come back in the same order the valid items were sent.
      for (let idx = 0; idx < valid.length; idx++) {
        const serverItem = created.items[idx];
        if (!serverItem) continue;
        for (const file of valid[idx].files) {
          const fd = new FormData();
          fd.append('file', file);
          await uploadReimbursementAttachment(serverItem.id, fd);
        }
      }
      setView('list');
      await fetchAll();
    } catch (err: any) {
      setCreateError(err.response?.data?.message || 'Error submitting claim');
    } finally {
      setCreating(false);
    }
  };

  // ── Workflow actions ───────────────────────────────────────────────────────

  const runAction = async (fn: () => Promise<any>) => {
    if (!detail) return;
    setActing(true);
    try {
      await fn();
    } catch (err: any) {
      alert(extractErrorMessage(err));
    } finally {
      setActing(false);
    }
    // Re-read either way: on failure the claim has usually moved on, and the
    // buttons must reflect its true status rather than the one we assumed.
    try {
      const res = await getReimbursement(detail.id);
      setDetail(res.data);
    } catch { /* claim gone */ }
    fetchAll();
  };

  const submitReason = async () => {
    if (!detail || !reasonFor) return;
    const kind = reasonFor;
    const text = reason;
    setReasonFor(null); setReason('');
    await runAction(() =>
      kind === 'RETURN'
        ? returnReimbursement(detail.id, { returnReason: text })
        : rejectReimbursement(detail.id, { rejectionReason: text }));
  };

  const attachToItem = async (itemId: number, files: File[]) => {
    if (!detail || files.length === 0) return;
    await runAction(async () => {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await uploadReimbursementAttachment(itemId, fd);
      }
    });
  };

  const canEdit = (r: Reimbursement) =>
    ['PENDING', 'REVIEWED', 'RETURNED'].includes(r.status) && r.staff.id === user?.staffId;

  // ── Views ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 bg-white border border-gray-200 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (view === 'create') {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => setView('list')} className="min-h-[44px] -ml-2 px-2 text-blue-600 text-sm font-medium">
            ‹ Back
          </button>
          <h1 className="text-lg font-bold text-gray-900">New claim</h1>
        </div>

        <div>
          <label className={LABEL} htmlFor="task-search">Task</label>
          {selectedTask ? (
            <div className="flex items-center gap-2 p-3 bg-white border border-gray-200 rounded-lg">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {selectedTask.taskName}
                  {selectedTask.categoryName ? ` (${selectedTask.categoryName})` : ''}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {selectedTask.taskId}{selectedTask.clientName ? ` · ${selectedTask.clientName}` : ''}
                </p>
              </div>
              <button
                onClick={() => { setTaskId(''); setTaskQuery(''); }}
                className="min-h-[44px] px-2 text-sm text-blue-600 shrink-0"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <input
                id="task-search"
                className={INPUT}
                value={taskQuery}
                onChange={(e) => setTaskQuery(e.target.value)}
                placeholder="Search task or client…"
                autoComplete="off"
              />
              {matchingTasks.length > 0 && (
                <div className="mt-1 bg-white border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
                  {matchingTasks.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => { setTaskId(t.id); setTaskQuery(''); }}
                      className="w-full text-left px-3 py-3 min-h-[44px] active:bg-blue-50"
                    >
                      <p className="text-sm text-gray-900 truncate">
                        {t.taskName}{t.categoryName ? ` (${t.categoryName})` : ''}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {t.taskId}{t.clientName ? ` · ${t.clientName}` : ''}
                      </p>
                    </button>
                  ))}
                </div>
              )}
              {taskQuery.trim() !== '' && matchingTasks.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No open task matches "{taskQuery}".</p>
              )}
            </>
          )}
        </div>

        {drafts.map((d, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Expense {idx + 1}
              </span>
              {drafts.length > 1 && (
                <button
                  onClick={() => setDrafts((prev) => prev.filter((_, i) => i !== idx))}
                  className="min-h-[44px] px-2 -mr-2 text-xs text-red-500"
                >
                  Remove
                </button>
              )}
            </div>

            <div>
              <span className={LABEL}>Receipt</span>
              <ReceiptPicker
                id={`draft-${idx}`}
                onFiles={(files) => patchDraft(idx, { files: [...d.files, ...files] })}
              />
              {d.files.length > 0 && (
                <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                  {d.files.map((f, fi) => (
                    <PendingThumb
                      key={`${f.name}-${fi}`}
                      file={f}
                      onRemove={() => patchDraft(idx, { files: d.files.filter((_, i) => i !== fi) })}
                    />
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className={LABEL}>Description</label>
              <input
                className={INPUT}
                value={d.description}
                onChange={(e) => patchDraft(idx, { description: e.target.value })}
                placeholder="e.g. Cab to client office"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Amount (₹)</label>
                <input
                  className={INPUT}
                  inputMode="decimal"
                  value={d.amount}
                  onChange={(e) => patchDraft(idx, { amount: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <label className={LABEL}>Date</label>
                <input
                  type="date"
                  className={INPUT}
                  value={d.date}
                  onChange={(e) => patchDraft(idx, { date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className={LABEL}>Category</label>
              <select
                className={INPUT}
                value={d.categoryId}
                onChange={(e) => patchDraft(idx, { categoryId: e.target.value })}
              >
                <option value="">— Select —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
        ))}

        <button
          onClick={() => setDrafts((prev) => [...prev, emptyDraft()])}
          className={`${BTN} w-full bg-white text-blue-700 border border-dashed border-blue-300`}
        >
          + Add another expense
        </button>

        <div>
          <label className={LABEL}>Notes (optional)</label>
          <input
            className={INPUT}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything the approver should know"
          />
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Claim total</span>
          <span className="font-bold text-gray-900">{fmt(draftTotal)}</span>
        </div>

        {createError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{createError}</p>
        )}

        <button
          onClick={handleCreate}
          disabled={creating}
          className={`${BTN} w-full bg-blue-600 text-white disabled:opacity-50`}
        >
          {creating ? 'Submitting…' : 'Submit claim'}
        </button>
      </div>
    );
  }

  if (view === 'detail' && detail) {
    const editable = canEdit(detail);
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => { setView('list'); setDetail(null); }} className="min-h-[44px] -ml-2 px-2 text-blue-600 text-sm font-medium">
            ‹ Claims
          </button>
          {detailLoading && <span className="text-xs text-gray-400">Refreshing…</span>}
        </div>

        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-blue-700">{detail.claimNumber}</span>
            <StatusPill status={detail.status} />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {detail.task.taskId} · {detail.task.taskName}
            {detail.task.category?.name ? ` (${detail.task.category.name})` : ''}
            {detail.task.client?.clientName ? ` · ${detail.task.client.clientName}` : ''}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {detail.staff.staffName} · {formatDateDdMmmYy(detail.createdAt)}
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{fmt(claimTotal(detail))}</p>
        </div>

        {detail.notes && (
          <p className="text-sm text-gray-700 bg-gray-100 rounded-lg px-3 py-2">{detail.notes}</p>
        )}

        {/* Approval trail — same set of states the desktop detail modal shows. */}
        <div className="space-y-2">
          {detail.status === 'RETURNED' && detail.returnReason && (
            <div className="text-sm text-orange-800 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
              <strong>Returned by {detail.reviewedBy?.staffName || 'HR'}:</strong> {detail.returnReason}
            </div>
          )}
          {detail.status === 'REJECTED' && detail.rejectionReason && (
            <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <strong>Rejected by {detail.approvedBy?.staffName || '—'}:</strong> {detail.rejectionReason}
            </div>
          )}
          {['REVIEWED', 'APPROVED', 'PAID'].includes(detail.status) && detail.reviewedBy && (
            <div className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              ✔ Reviewed by <strong>{detail.reviewedBy.staffName}</strong>
              {detail.reviewedAt ? ` on ${formatDateDdMmmYy(detail.reviewedAt)}` : ''}
            </div>
          )}
          {['APPROVED', 'PAID'].includes(detail.status) && detail.approvedBy && (
            <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              ✔ Approved by <strong>{detail.approvedBy.staffName}</strong>
              {detail.approvedAt ? ` on ${formatDateDdMmmYy(detail.approvedAt)}` : ''}
            </div>
          )}
          {detail.status === 'PAID' && (
            <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              💰 Paid {formatDateDdMmmYy(detail.paidOn)}
              {detail.paymentMode ? ` via ${detail.paymentMode}` : ''}
              {detail.paymentReference ? ` (${detail.paymentReference})` : ''}
            </div>
          )}
          {editable && detail.status === 'REVIEWED' && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              ⚠️ Already reviewed by HR. Adding a receipt sends it back to Pending review.
            </div>
          )}
        </div>

        <div className="space-y-3">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Line items</h2>
          {detail.items.map((item) => (
            <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 break-words">{item.description}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {item.category?.name} · {formatDateDdMmmYy(item.date)}
                  </p>
                </div>
                <span className="text-sm font-semibold text-gray-900 shrink-0">{fmt(item.amount)}</span>
              </div>

              {item.attachments.length > 0 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                  {item.attachments.map((att) => (
                    <a
                      key={att.id}
                      href={uploadsUrl(att.fileName)}
                      target="_blank"
                      rel="noreferrer"
                      className="w-20 h-20 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden shrink-0 flex items-center justify-center"
                    >
                      {att.mimeType.startsWith('image/') ? (
                        <img src={uploadsUrl(att.fileName)} alt={att.originalName} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-2xl">📄</span>
                      )}
                    </a>
                  ))}
                </div>
              )}

              {editable && (
                <div className="mt-3">
                  <ReceiptPicker id={`item-${item.id}`} onFiles={(files) => attachToItem(item.id, files)} />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-2 pt-2">
          {isHR && detail.status === 'PENDING' && detail.staff.id !== user?.staffId && (
            <>
              <button
                disabled={acting}
                onClick={() => runAction(() => reviewReimbursement(detail.id))}
                className={`${BTN} w-full bg-blue-600 text-white disabled:opacity-50`}
              >
                ✔ Mark reviewed
              </button>
              <button
                disabled={acting}
                onClick={() => { setReason(''); setReasonFor('RETURN'); }}
                className={`${BTN} w-full bg-orange-500 text-white disabled:opacity-50`}
              >
                ↩ Return to staff
              </button>
            </>
          )}
          {isApprover && detail.status === 'REVIEWED' && (
            <>
              <button
                disabled={acting}
                onClick={() => {
                  if (!window.confirm('Approve this claim? Expenses will be added to the task.')) return;
                  runAction(() => approveReimbursement(detail.id));
                }}
                className={`${BTN} w-full bg-green-600 text-white disabled:opacity-50`}
              >
                ✔ Approve claim
              </button>
              <button
                disabled={acting}
                onClick={() => { setReason(''); setReasonFor('RETURN'); }}
                className={`${BTN} w-full bg-orange-500 text-white disabled:opacity-50`}
              >
                ↩ Send back to staff
              </button>
              <button
                disabled={acting}
                onClick={() => { setReason(''); setReasonFor('REJECT'); }}
                className={`${BTN} w-full bg-red-600 text-white disabled:opacity-50`}
              >
                ✕ Reject
              </button>
            </>
          )}
          {detail.status === 'RETURNED' && detail.staff.id === user?.staffId && (
            <button
              disabled={acting}
              onClick={() => runAction(() => resubmitReimbursement(detail.id))}
              className={`${BTN} w-full bg-blue-600 text-white disabled:opacity-50`}
            >
              ↪ Resubmit for review
            </button>
          )}
          {detail.status === 'REJECTED' && (detail.staff.id === user?.staffId || isApprover) && (
            <button
              disabled={acting}
              onClick={() => { if (window.confirm('Reopen this rejected claim? It goes back to the employee as Returned.')) runAction(() => reopenReimbursement(detail.id)); }}
              className={`${BTN} w-full bg-orange-500 text-white disabled:opacity-50`}
            >
              ↺ Reopen claim
            </button>
          )}
        </div>

        {reasonFor && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end" onClick={() => setReasonFor(null)}>
            <div
              className="w-full bg-white rounded-t-2xl p-4 space-y-3"
              style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-base font-semibold text-gray-900">
                {reasonFor === 'RETURN' ? 'Return to staff' : 'Reject claim'}
              </h3>
              <textarea
                className={`${INPUT} min-h-[96px]`}
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={reasonFor === 'RETURN' ? 'What needs correcting?' : 'Reason for rejection (optional)'}
              />
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setReasonFor(null)} className={`${BTN} bg-gray-100 text-gray-700`}>
                  Cancel
                </button>
                <button
                  onClick={submitReason}
                  className={`${BTN} text-white ${reasonFor === 'RETURN' ? 'bg-orange-500' : 'bg-red-600'}`}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-gray-900">Claims</h1>
        <button onClick={openCreate} className={`${BTN} bg-blue-600 text-white`}>
          📷 New claim
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`min-h-[36px] px-3 rounded-full text-xs font-semibold border ${
              filter === f.key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loadError ? (
        <div className="bg-white border border-red-200 rounded-xl p-6 text-center space-y-3">
          <p className="text-sm text-red-600">{loadError}</p>
          <button
            onClick={() => { setLoading(true); fetchAll(); }}
            className={`${BTN} bg-blue-600 text-white mx-auto`}
          >
            Try again
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center space-y-3">
          <p className="text-3xl" aria-hidden="true">🧾</p>
          <p className="text-sm text-gray-500">
            {filter ? 'No claims match this filter.' : 'No claims yet. Snap a receipt to file your first one.'}
          </p>
          {!filter && (
            <button onClick={openCreate} className={`${BTN} bg-blue-600 text-white mx-auto`}>
              📷 File a claim
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((r) => {
            const mine = r.staff.id === user?.staffId;
            const needsMe = r.status === 'RETURNED' && mine;
            return (
              <button
                key={r.id}
                onClick={() => openDetail(r)}
                className={`w-full text-left bg-white border rounded-xl p-4 active:bg-gray-50 ${
                  needsMe ? 'border-orange-300 ring-1 ring-orange-200' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-semibold text-blue-700">{r.claimNumber}</p>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {r.task.client?.clientName || r.task.taskName}
                    </p>
                  </div>
                  <StatusPill status={r.status} />
                </div>
                <div className="flex items-baseline justify-between gap-3 mt-2">
                  <span className="text-lg font-bold text-gray-900">{fmt(claimTotal(r))}</span>
                  <span className="text-xs text-gray-400">{formatDateDdMmmYy(r.createdAt)}</span>
                </div>
                {needsMe && (
                  <p className="text-xs font-semibold text-orange-600 mt-2">Needs your attention — tap to fix</p>
                )}
                {!mine && (
                  <p className="text-xs text-gray-400 mt-1 truncate">{r.staff.staffName}</p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileReimbursements;
